// Slide Cart Functionality

// Shopper-facing copy, kept identical to limitLabelPurchased in
// sections/product.liquid. The product page owns the label for its own limit-N
// controls; this is the fallback for buttons nothing else claims (drawer
// recommendation cards, pages without the product-page guard).
// N = 1 renders exactly 'LIMIT 1 \u00b7 ALREADY PURCHASED', as before limit-N.
function slideCartLimitLabelPurchased(limit) {
  return `LIMIT ${limit} \u00b7 ALREADY PURCHASED`;
}

// Plus-button tooltip. N = 1 keeps the original wording.
function slideCartLimitTitle(limit) {
  return limit === 1 ? 'Limit one per customer' : `Limit ${limit} per customer`;
}

// A product's cap from a data-limit value: 1..12 as the validation function
// recognizes (tags limit-1 .. limit-12), else 0 (no limit).
function parseLimit(value) {
  const text = String(value ?? '');
  return /^(?:[1-9]|1[0-2])$/.test(text) ? Number(text) : 0;
}

// The cart-and-checkout validation function's message (contract with the
// function): "Limit one per customer. This product was already purchased." for
// N = 1, "Limit {N} per customer. You have reached the limit for this product."
// for N > 1. Matched anywhere and case-insensitively, as limit-1 always was,
// because the exact wording of the Ajax /cart/add.js description is not proven.
// Matching only this family keeps other 422s (another app's rule, a stock error)
// transient.
function isLimitVerdict(message) {
  return typeof message === 'string' && /limit (one|\d+) per customer/i.test(message);
}

class SlideCart {
  constructor() {
    this.cart = {
      items: [],
      total: 0,
      count: 0
    };
    this.pendingLimitProducts = new Set();
    // product id -> N for every limit-N product this page knows about.
    this.limitByProduct = new Map();
    this.cartRequestSeq = 0;
    
    this.elements = {
      overlay: document.getElementById('cart-overlay'),
      slideCart: document.getElementById('slide-cart'),
      closeBtn: document.getElementById('close-cart'),
      clearBtn: document.getElementById('clear-cart'),
      cartItems: document.getElementById('cart-items'),
      cartCount: document.getElementById('cart-count'),
      cartSubtotal: document.getElementById('cart-subtotal'),
      cartSavings: document.getElementById('cart-savings'),
      savingsAmount: document.getElementById('savings-amount'),
      checkoutBtn: document.getElementById('checkout-btn'),
      shippingEstimate: document.getElementById('shipping-estimate'),
      shippingCost: document.getElementById('shipping-cost'),
      shippingTooltipBtn: document.getElementById('shipping-tooltip-btn'),
      shippingTooltip: document.getElementById('shipping-tooltip')
    };
    
    // Shipping rates per bottle
    this.shippingRates = [
      { min: 1, max: 2, cost: 24.99 },
      { min: 3, max: 3, cost: 29.99 },
      { min: 4, max: 6, cost: 39.99 },
      { min: 7, max: 11, cost: 45.99 }
    ];
    
    this.init();
  }

  init() {
    this.loadKnownLimitProducts();
    this.bindEvents();
    this.initTooltip();
    this.loadCart();
    this.addScrollbarStyles();
  }

  loadKnownLimitProducts() {
    document.querySelectorAll('[data-limit][data-product-id]').forEach(button => {
      this.rememberLimit(button.dataset.productId, button.dataset.limit);
    });
    try {
      const source = document.querySelector('[data-slide-cart-limit-products]');
      const limits = source ? JSON.parse(source.textContent) : null;
      if (limits && typeof limits === 'object' && !Array.isArray(limits)) {
        Object.entries(limits).forEach(([id, limit]) => this.rememberLimit(id, limit));
      }
    } catch { /* Optional server hint; product controls still identify limited items. */ }
  }

  // Remembers a product's cap; if two sources disagree, the smaller cap wins.
  rememberLimit(productId, value) {
    const limit = parseLimit(value);
    if (!productId || !limit) return;
    const id = String(productId);
    const known = this.limitByProduct.get(id);
    if (!known || limit < known) this.limitByProduct.set(id, limit);
  }

  // The product's cap N, or 0 when it is not a limited product.
  limitFor(productId) {
    return this.limitByProduct.get(String(productId)) || 0;
  }

  productCount(productId) {
    return this.cart.items.reduce((count, item) =>
      String(item.product_id) === String(productId) ? count + item.quantity : count, 0);
  }

  // Units of each product bought inside the window; empty when history is
  // missing or unreadable (fail open).
  historyQuantities() {
    try {
      const snapshot = globalThis.purchaseLimitHistory?.readSnapshot(document);
      return snapshot?.status === 'ok' && snapshot.quantities instanceof Map ? snapshot.quantities : new Map();
    } catch { return new Map(); }
  }

  historyQuantity(productId, quantities = this.historyQuantities()) {
    return quantities.get(String(productId)) || 0;
  }

  // True when another unit would take the product past its cap:
  // bought in window + already in cart >= N.
  atLimit(productId, quantities) {
    const limit = this.limitFor(productId);
    return limit > 0 && this.historyQuantity(productId, quantities) + this.productCount(productId) >= limit;
  }

  announceCartChange() {
    document.dispatchEvent(new CustomEvent('slide-cart:updated', {detail: {cart: this.cart}}));
  }
  
  bindEvents() {
    // Close cart events
    this.elements.closeBtn?.addEventListener('click', () => this.closeCart());
    this.elements.overlay?.addEventListener('click', () => this.closeCart());

    // Clear cart event
    this.elements.clearBtn?.addEventListener('click', () => this.clearCart());
    
    // Add to cart buttons (will be added dynamically to product buttons)
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('add-to-cart-btn') || e.target.closest('.add-to-cart-btn')) {
        // A control hidden with the `hidden` attribute (the release gate swaps
        // a card's button for a label that way) never adds, even when a script
        // clicks it.
        if ((e.target.closest('.add-to-cart-btn') || e.target).closest('[hidden]')) return;
        e.preventDefault();
        e.stopPropagation();
        this.handleAddToCart(e.target.closest('.add-to-cart-btn') || e.target);
      }
      
      // Cart icon clicks
      if (e.target.classList.contains('cart-icon') || e.target.closest('.cart-icon')) {
        e.preventDefault();
        this.openCart();
      }
    });
    
    // Checkout button
    this.elements.checkoutBtn?.addEventListener('click', () => {
      if (this.cart.items.length > 0) {
        window.location.href = '/checkout';
      }
    });
  }
  
  
  initTooltip() {
    if (!this.elements.shippingTooltipBtn || !this.elements.shippingTooltip) return;
    
    // Show tooltip on hover
    this.elements.shippingTooltipBtn.addEventListener('mouseenter', () => {
      this.elements.shippingTooltip.classList.remove('opacity-0', 'invisible');
      this.elements.shippingTooltip.classList.add('opacity-100', 'visible');
    });
    
    // Hide tooltip when not hovering
    this.elements.shippingTooltipBtn.addEventListener('mouseleave', () => {
      this.elements.shippingTooltip.classList.remove('opacity-100', 'visible');
      this.elements.shippingTooltip.classList.add('opacity-0', 'invisible');
    });
    
    // Keep tooltip visible when hovering over tooltip itself
    this.elements.shippingTooltip.addEventListener('mouseenter', () => {
      this.elements.shippingTooltip.classList.remove('opacity-0', 'invisible');
      this.elements.shippingTooltip.classList.add('opacity-100', 'visible');
    });
    
    this.elements.shippingTooltip.addEventListener('mouseleave', () => {
      this.elements.shippingTooltip.classList.remove('opacity-100', 'visible');
      this.elements.shippingTooltip.classList.add('opacity-0', 'invisible');
    });
  }
  
  addScrollbarStyles() {
    // Add scrollbar styles for webkit browsers
    const style = document.createElement('style');
    style.textContent = `
      #slide-cart .overflow-y-auto::-webkit-scrollbar {
        width: 4px;
      }
      #slide-cart .overflow-y-auto::-webkit-scrollbar-track {
        background: #f1f5f9;
      }
      #slide-cart .overflow-y-auto::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
      }
      #slide-cart .overflow-y-auto::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    `;
    document.head.appendChild(style);
  }
  
  
  async handleAddToCart(button) {
    // For buttons with price spans, only capture the main text content
    let originalText = button.textContent;
    const firstSpan = button.querySelector('span.tracking-wide');
    if (firstSpan) {
      originalText = firstSpan.textContent;
    }
    let productId;
    let isLimited = false;
    let limit = 0;
    let ownsLimitLock = false;
    try {
      productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      isLimited = parseLimit(button.dataset.limit) > 0;
      if (isLimited && productId) this.rememberLimit(productId, button.dataset.limit);
      limit = isLimited ? this.limitFor(productId) : 0;
      // One limited add in flight per product, and none once bought + in cart reaches N.
      if (isLimited && (this.pendingLimitProducts.has(String(productId)) || this.atLimit(productId))) return;
      if (isLimited) {
        this.pendingLimitProducts.add(String(productId));
        ownsLimitLock = true;
      }
      const quantity = isLimited ? 1 : (parseInt(button.dataset.quantity) || 1);
      
      // Check if variant ID is valid
      if (!variantId || variantId === '') {
        throw new Error('No variant selected');
      }
      
      // Show loading state
      const spanToUpdate = button.querySelector('span.tracking-wide') || button;
      spanToUpdate.textContent = 'Adding...';
      button.disabled = true;
      
      // Prepare cart item data
      const cartItem = {
        id: variantId,
        quantity: quantity
      };

      if (button.dataset.properties) {
        try {
          const properties = JSON.parse(button.dataset.properties);
          if (properties && typeof properties === 'object' && !Array.isArray(properties)) cartItem.properties = properties;
        } catch { /* Ignore malformed optional line properties. */ }
      }
      
      // Add selling plan for subscription products
      if (button.dataset.sellingPlan) {
        cartItem.selling_plan = button.dataset.sellingPlan;
      }
      
      
      // Add to Shopify cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartItem)
      });
      
      if (response.ok) {
        // Widgets use this to count their own adds (by the _source property).
        try { document.dispatchEvent(new CustomEvent('slide-cart:added', {detail: {variantId, properties: cartItem.properties || null}})); } catch { /* never block an add */ }
        // Check if cart is already open
        const isCartOpen = !this.elements.slideCart?.classList.contains('translate-x-full');
        
        // Update local cart
        await this.loadCart();
        
        
        // Only open cart if it's not already open (for regular product adds)
        if (!isCartOpen) {
          this.openCart();
        }
        
        // Limit-1 controls were reconciled from the confirmed cart above.
        // Keep their authoritative "already in cart" state; ordinary products
        // retain the existing short success feedback. A limit-N (N > 1) control
        // still under its cap gets the ordinary feedback too; at the cap it
        // keeps whatever its owner painted, or says why it stopped.
        const limitN = isLimited && limit > 1;
        if (limitN && this.atLimit(productId)) {
          const target = button.querySelector('span.tracking-wide') || button;
          if (target.textContent === 'Adding...') {
            this.setButtonLabel(button, `LIMIT ${limit} \u00b7 IN CART`);
            button.disabled = true;
          }
        } else if (!isLimited || limitN) {
          const spanToUpdate = button.querySelector('span.tracking-wide') || button;
          spanToUpdate.textContent = 'Added!';
          setTimeout(() => {
          spanToUpdate.textContent = originalText;
          button.disabled = false;
          }, 1000);
        }
      } else {
        // Shopify runs the cart-and-checkout validation on CART mutations too,
        // not only at checkout, whenever the cart carries a buyer email (it does
        // once the shopper has started a checkout in this browser). So a
        // limit-1 product the customer already bought comes back here as a 422
        // carrying the validation's own message. That is a real answer, not a
        // transient failure, and flashing 'Error' for two seconds and resetting
        // is what Adam saw as "the button does nothing".
        const message = await this.readAddFailureMessage(response);
        // `handled` is written back by whichever listener takes ownership of the
        // button — the product page's limit-1 guard does, for its own controls.
        // Listeners run synchronously, so it is settled by the time dispatch
        // returns.
        const detail = {productId, status: response.status, message, handled: false};
        document.dispatchEvent(new CustomEvent('slide-cart:add-failed', {detail}));

        // Only OUR validation's verdict earns the purchased label. Other apps'
        // validations and Shopify's own stock errors also answer 422 with a
        // sentence, and those must stay transient.
        const serverBlocked = isLimited && response.status === 422 && isLimitVerdict(message);
        if (serverBlocked && detail.handled) {
          // The product page owns this button now: it paints the purchased
          // label and keeps it disabled. Touching it here would fight that.
          return;
        }
        if (serverBlocked) {
          // Nobody claimed it — a drawer recommendation card, or a page without
          // the product-page guard. Say the same thing rather than 'Error',
          // and leave it disabled: the answer will not change on a retry.
          this.setButtonLabel(button, slideCartLimitLabelPurchased(limit));
          button.disabled = true;
          return;
        }
        // Everything else — 5xx, a 422 with no readable message, anything
        // unexpected — keeps the existing transient-failure behaviour.
        this.resetButtonAfterFailure(button, originalText);
        return;
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.resetButtonAfterFailure(button, originalText);
      document.dispatchEvent(new CustomEvent('slide-cart:add-failed', {detail: {productId, status: null, message: null, handled: false}}));
    } finally {
      if (ownsLimitLock) this.pendingLimitProducts.delete(String(productId));
    }
  }

  // Cart-validation rejections come back from the Ajax API as
  // {status: 422, message: 'Cart Error', description: '<validation message>'},
  // but nothing guarantees that shape, so read it defensively and return null
  // whenever there is no usable sentence to show a shopper.
  async readAddFailureMessage(response) {
    try {
      const body = await response.json();
      if (body && typeof body === 'object') {
        const message = body.description || body.message;
        if (typeof message === 'string' && message.trim()) return message.trim();
      }
    } catch { /* Not JSON, empty, or already consumed. */ }
    return null;
  }

  setButtonLabel(button, label) {
    const target = button.querySelector('span.tracking-wide') || button;
    target.textContent = label;
  }

  // The long-standing transient-failure behaviour, unchanged: say 'Error' for
  // two seconds, then restore the button.
  resetButtonAfterFailure(button, originalText) {
    this.setButtonLabel(button, 'Error');
    setTimeout(() => {
      this.setButtonLabel(button, originalText);
      button.disabled = false;
    }, 2000);
  }
  
  // Optimistic cart edits: mutate the local cart, repaint immediately, then
  // send the change. Only the latest in-flight response is applied, and any
  // failure reloads the authoritative cart from Shopify.
  recomputeLocalTotals() {
    this.cart.count = this.cart.items.reduce((n, item) => n + item.quantity, 0);
    this.cart.total = this.cart.items.reduce((n, item) => n + item.final_line_price, 0);
  }

  applyLocalQuantity(item, quantity) {
    item.quantity = quantity;
    item.final_line_price = item.final_price * quantity;
    item.original_line_price = item.original_price * quantity;
    item.line_price = item.original_line_price;
    this.recomputeLocalTotals();
    this.updateCartDisplay();
  }

  async sendCartUpdate(updates) {
    const seq = ++this.cartRequestSeq;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({updates})
      });
      if (seq !== this.cartRequestSeq) return; // a newer edit supersedes this response
      if (response.ok) {
        this.applyCart(await response.json());
      } else {
        console.error('Cart update failed:', response.status, await response.text());
        await this.loadCart();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      if (seq === this.cartRequestSeq) await this.loadCart();
    }
  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      return this.applyCart(await response.json());
    } catch (error) {
      console.error('Error loading cart:', error);
      return null;
    }
  }

  applyCart(cartData) {
    this.cart = {
      items: cartData.items,
      total: cartData.total_price,
      count: cartData.item_count
    };
    this.updateCartDisplay();
    this.announceCartChange();
    return this.cart;
  }
  
  updateCartDisplay() {
    // Update cart count in slide cart
    this.elements.cartCount.textContent = this.cart.count;
    
    // Update cart count in header
    this.updateHeaderCartCount();
    
    // Calculate corrected subtotal
    const correctedSubtotal = this.calculateCorrectedSubtotal();
    this.elements.cartSubtotal.textContent = this.formatPrice(correctedSubtotal);
    
    // Update shipping estimate
    this.updateShippingEstimate();
    
    // Update checkout button state
    this.elements.checkoutBtn.disabled = this.cart.count === 0;
    
    
    // Update cart items
    this.renderCartItems();
  }
  
  
  calculateCorrectedSubtotal() {
    let total = 0;
    
    this.cart.items.forEach(item => {
      total += item.final_line_price;
    });
    
    return total;
  }
  
  updateHeaderCartCount() {
    // Update the header cart count
    const headerCartCounts = document.querySelectorAll('.cart-count');
    headerCartCounts.forEach(element => {
      element.textContent = this.cart.count;
    });
  }
  
  renderCartItems() {
    if (this.cart.items.length === 0) {
      this.elements.cartItems.innerHTML = `
        <div class="slide-cart-empty text-center py-8">
          <p>Your cart is empty</p>
        </div>
      `;
      return;
    }
    
    const quantities = this.historyQuantities();
    const itemsHTML = this.cart.items.map(item => this.renderCartItem(item, quantities)).join('');
    this.elements.cartItems.innerHTML = itemsHTML;
    
    // Bind quantity change events
    this.bindQuantityEvents();
  }
  
  renderCartItem(item, quantities = this.historyQuantities()) {
    let originalPrice = item.original_line_price;
    let currentPrice = item.final_line_price;
    let unitPrice = item.final_price;
    let hasDiscount = originalPrice > currentPrice;
    // Plus stops once this product's units in the cart reach N - bought.
    const limit = this.limitFor(item.product_id);
    const plusCapped = this.atLimit(item.product_id, quantities);
    
    return `
      <div class="cart-item flex gap-3 py-3 border-b border-gray-100" data-key="${item.key}" style="transition: all 0.2s ease-in-out;">
        <div class="flex-shrink-0">
          <img src="${item.featured_image?.url}" alt="${item.product_title}" 
               class="w-20 h-24 object-contain rounded-md bg-white">
        </div>
        
        <div class="flex-1 min-w-0">
          <div class="flex items-start justify-between mb-1">
            <div class="flex-1">
              <h3 class="font-medium text-base text-seelblue">${item.product_title}</h3>
            </div>
            <div class="text-right ml-2">
              ${hasDiscount ? `<div><span class="text-sm text-gray-500 line-through">${this.formatPrice(originalPrice)}</span></div>` : ''}
              <span class="text-base font-medium">${this.formatPrice(currentPrice)}</span>
            </div>
          </div>
          ${item.selling_plan_allocation ? `
            <div class="flex items-center">
              <svg class="w-3 h-3 text-green-600 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span class="text-green-800 text-sm font-medium">${item.selling_plan_allocation.selling_plan.name}</span>
            </div>
          ` : ''}
          <p class="text-sm text-gray-600 mb-2">${item.variant_title || ''}</p>

          <div class="flex items-center mt-2">
            <button class="quantity-btn minus w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 bg-gray-100"
                    data-key="${item.key}" data-action="decrease" style="transition: all 0.2s ease-in-out;">−</button>
            <span class="quantity-display w-8 text-center font-medium mx-1">${item.quantity}</span>
            <button class="quantity-btn plus w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 bg-gray-100"
                    data-key="${item.key}" data-action="increase" ${plusCapped ? `disabled aria-disabled="true" title="${slideCartLimitTitle(limit)}"` : ''}
                    style="transition: all 0.2s ease-in-out;${plusCapped ? ' opacity: 0.4; cursor: not-allowed;' : ''}">+</button>
            <button class="remove-item text-gray-600 hover:text-red-600 transition-colors ml-3" aria-label="Remove" data-key="${item.key}">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }
  
  bindQuantityEvents() {
    // Quantity buttons
    document.querySelectorAll('.quantity-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        // Tiny press feedback: button dips, quantity bumps.
        btn.classList.add('is-pressed');
        const display = btn.parentElement?.querySelector('.quantity-display');
        display?.classList.add('is-bumped');
        setTimeout(() => { btn.classList.remove('is-pressed'); display?.classList.remove('is-bumped'); }, 180);
        this.updateQuantity(key, action);
      });
    });
    
    // Cart item hover effects
    document.querySelectorAll('.cart-item').forEach(item => {
      item.style.transition = 'all 0.2s ease-in-out';
    });
    
    // Remove buttons
    document.querySelectorAll('.remove-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        this.removeItem(key);
      });
    });
  }
  
  async updateQuantity(key, action) {
    const item = this.cart.items.find(item => item.key === key);
    if (!item) return;
    if (action === 'increase' && this.atLimit(item.product_id)) return;

    let newQuantity = item.quantity;
    if (action === 'increase') newQuantity++;
    else if (action === 'decrease' && newQuantity > 1) newQuantity--;
    else if (action === 'decrease' && newQuantity === 1) { this.removeItem(key); return; }
    else return;

    this.applyLocalQuantity(item, newQuantity);
    await this.sendCartUpdate({[key]: newQuantity});
  }

  async removeItem(key) {
    const itemToRemove = this.cart.items.find(item => item.key === key);
    if (!itemToRemove) return;

    this.cart.items = this.cart.items.filter(item => item.key !== key);
    this.recomputeLocalTotals();
    this.updateCartDisplay();
    await this.sendCartUpdate({[key]: 0});
  }

  calculateShipping(bottleCount) {
    if (bottleCount === 0) return 0;
    
    const rate = this.shippingRates.find(rate => 
      bottleCount >= rate.min && bottleCount <= rate.max
    );
    
    // If more than 11 bottles, use the highest rate (could be adjusted later)
    return rate ? rate.cost : this.shippingRates[this.shippingRates.length - 1].cost;
  }
  
  updateShippingEstimate() {
    const bottleCount = this.cart.count;
    
    if (bottleCount > 0) {
      const shippingCost = this.calculateShipping(bottleCount);
      this.elements.shippingCost.textContent = `$${shippingCost.toFixed(2)}`;
      this.elements.shippingCost.classList.remove('text-green-600', 'font-medium');
      
      this.elements.shippingEstimate?.classList.remove('hidden');
    } else {
      this.elements.shippingEstimate?.classList.add('hidden');
    }
  }
  
  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }
  
  openCart() {
    this.elements.overlay?.classList.remove('hidden');
    this.elements.slideCart?.classList.remove('translate-x-full');
    document.body.style.overflow = 'hidden';
    document.dispatchEvent(new CustomEvent('slide-cart:opened', {detail: {cart: this.cart}}));
  }
  
  closeCart() {
    this.elements.overlay?.classList.add('hidden');
    this.elements.slideCart?.classList.add('translate-x-full');
    document.body.style.overflow = '';
  }

  async clearCart() {
    try {
      // Update button state
      const originalText = this.elements.clearBtn.textContent;
      this.elements.clearBtn.textContent = 'Clearing...';
      this.elements.clearBtn.disabled = true;

      // Clear cart by setting all items to 0
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        this.applyCart(await response.json());

        // Reset button
        this.elements.clearBtn.textContent = originalText;
        this.elements.clearBtn.disabled = false;

      } else {
        throw new Error('Failed to clear cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      this.elements.clearBtn.textContent = 'Error';
      setTimeout(() => {
        this.elements.clearBtn.textContent = 'Clear';
        this.elements.clearBtn.disabled = false;
      }, 2000);
    }
  }
}

// Initialize slide cart when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.slideCart = new SlideCart();
});

// Global function to open cart (can be called from anywhere)
window.openSlideCart = () => {
  if (window.slideCart) {
    window.slideCart.openCart();
  }
};
